import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FaArrowLeft, FaCheckCircle, FaClock, FaExclamationTriangle } from 'react-icons/fa';
import EMIService from '../../service/emiService';
import styled from 'styled-components';

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  background: #f7fafc;
  padding: 2rem 1rem;
  
  @media (max-width: 768px) {
    padding: 1rem 0.5rem;
  }
`;

const ContentWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: white;
  border: 2px solid #e2e8f0;
  padding: 0.75rem 1.5rem;
  border-radius: 50px;
  font-weight: 600;
  color: #4a5568;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover {
    background: #f7fafc;
    border-color: #667eea;
    color: #667eea;
    transform: translateX(-5px);
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.9rem;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 20px;
  padding: 2.5rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 800;
  color: #1a202c;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const SubTitle = styled.p`
  font-size: 1.1rem;
  color: #4a5568;
  margin-bottom: 2rem;
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 50px;
  font-weight: 700;
  font-size: 0.9rem;
  margin-bottom: 1.5rem;
  
  &.overdue {
    background: #fee2e2;
    color: #991b1b;
  }
  
  &.grace {
    background: #fef3c7;
    color: #92400e;
  }
  
  &.active {
    background: #d1fae5;
    color: #065f46;
  }
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const DetailCard = styled.div`
  background: ${props => props.bgColor || '#f7fafc'};
  border-radius: 12px;
  padding: 1.5rem;
  border: 2px solid ${props => props.borderColor || '#e2e8f0'};
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const DetailLabel = styled.div`
  font-size: 0.85rem;
  color: #64748b;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.5rem;
`;

const DetailValue = styled.div`
  font-size: 2rem;
  font-weight: 800;
  color: ${props => props.color || '#1a202c'};
  font-family: 'Poppins', sans-serif;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const PayButton = styled.button`
  width: 100%;
  padding: 1.25rem 2rem;
  font-size: 1.3rem;
  font-weight: 700;
  color: white;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border: none;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 10px 30px rgba(16, 185, 129, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 40px rgba(16, 185, 129, 0.4);
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
    transform: none;
  }
  
  @media (max-width: 768px) {
    padding: 1rem 1.5rem;
    font-size: 1.1rem;
  }
`;

const LoadingSpinner = styled.div`
  border: 3px solid #f3f4f6;
  border-top-color: #667eea;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 2rem auto;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const EMIPaymentPage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [emiStatus, setEmiStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    // Try to get EMI status from navigation state first
    const passed = location.state?.emiStatus;
    // State handed over by the lock screens only covers the overdue case; for
    // anything else (pre-payment) fetch the full status incl. nextEmi.
    if (passed && (passed.totalOverdue > 0 || passed.nextEmiState)) {
      setEmiStatus(passed);
      setLoading(false);
    } else {
      fetchEmiStatus();
    }
  }, [courseId]);

  const fetchEmiStatus = async () => {
    try {
      setLoading(true);
      const response = await EMIService.getEMIStatus(courseId);
      
      if (response.success) {
        setEmiStatus(response.data);
      } else {
        setError(response.message || 'Failed to fetch EMI status');
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Please log in to Schoolemy and then open this page again.');
      } else {
        setError(err.response?.data?.message || err.message || 'Failed to load EMI details');
      }
    } finally {
      setLoading(false);
    }
  };

  // Loads Paytm's CheckoutJS the same way the main purchase flow does
  // (Payment.js) - this page must speak the same gateway shape (txnToken +
  // orderId), not the pre-migration Cashfree shape it previously expected.
  const loadPaytmScript = () => {
    return new Promise((resolve, reject) => {
      if (window.Paytm && window.Paytm.CheckoutJS) {
        return resolve(true);
      }

      const isProduction =
        process.env.REACT_APP_PAYTM_ENV?.toLowerCase() === "production";
      const paytmBaseUrl = isProduction
        ? "https://secure.paytmpayments.com"
        : "https://securestage.paytmpayments.com";
      const mid = process.env.REACT_APP_PAYTM_MID;

      if (!mid) {
        reject(new Error("Paytm MID is not configured. Check REACT_APP_PAYTM_MID env var."));
        return;
      }

      const script = document.createElement("script");
      script.type = "application/javascript";
      script.crossOrigin = "anonymous";
      script.src = `${paytmBaseUrl}/merchantpgpui/checkoutjs/merchants/${mid}.js`;
      script.async = true;

      const timeoutId = setTimeout(() => {
        script.onload = null;
        script.onerror = null;
        reject(new Error("Timeout loading Paytm SDK. Please check your internet connection."));
      }, 12000);

      script.onload = () => {
        clearTimeout(timeoutId);
        if (window.Paytm && window.Paytm.CheckoutJS) resolve(true);
        else reject(new Error("Paytm SDK loaded but CheckoutJS not in window."));
      };
      script.onerror = () => {
        clearTimeout(timeoutId);
        reject(new Error("Failed to load Paytm SDK. Please check your internet connection."));
      };

      document.head.appendChild(script);
    });
  };

  const handlePayment = async () => {
    try {
      setPaying(true);

      // Overdue EMIs -> lock-screen flow. Otherwise it is a pre-payment of the
      // next EMI: the backend is told WHICH installment (emiId) and decides the
      // amount and whether it may be paid now.
      const isEarlyPayment = !(emiStatus.totalOverdue > 0);
      if (isEarlyPayment && (emiStatus.nextEmiState !== 'payable' || !emiStatus.nextEmi?.emiId)) {
        setPaying(false);
        return;
      }
      const amountToPay = isEarlyPayment ? emiStatus.nextEmi.amount : emiStatus.totalOverdue;

      const scriptLoaded = await loadPaytmScript();
      if (!scriptLoaded || !window.Paytm?.CheckoutJS) {
        throw new Error("Payment gateway is not available. Please refresh the page and try again.");
      }

      const response = isEarlyPayment
        ? await EMIService.payMonthlyEmi(courseId, emiStatus.nextEmi.emiId)
        : await EMIService.payOverdueEMIs(courseId, amountToPay);

      if (!response.success) {
        alert(response.message || "Payment initiation failed");
        setPaying(false);
        return;
      }

      // Paytm returns txnToken + orderId (see Payment-Controller/EmiController),
      // not a Cashfree payment_session_id.
      const { txnToken, orderId, paymentId } = response;
      if (!txnToken || !orderId || !paymentId) {
        throw new Error("Invalid payment session received from server");
      }

      // PaymentCallback.js (the shared /payment/callback page every Paytm
      // order redirects through) reads this to know which course/payment to verify.
      localStorage.setItem(
        "currentPaymentDetails",
        JSON.stringify({
          orderId: String(orderId),
          paymentId: String(paymentId),
          courseId: String(courseId),
          paymentType: "emi_installment",
          amount: amountToPay,
          isTutorCourse: false,
        }),
      );

      await window.Paytm.CheckoutJS.init({
        root: "",
        flow: "DEFAULT",
        merchant: {
          mid: process.env.REACT_APP_PAYTM_MID,
          redirect: true,
        },
        data: {
          orderId: String(orderId),
          token: txnToken,
          tokenType: "TXN_TOKEN",
          amount: Number(amountToPay).toFixed(2),
        },
        // Paytm's CheckoutJS requires this handler - init() throws
        // "notifyMerchant function handler is mandatory" without it.
        handler: {
          notifyMerchant: function (eventName, data) {
            console.log("Paytm notifyMerchant:", eventName, data);
          },
        },
      });

      window.Paytm.CheckoutJS.invoke();
      // Paytm takes over the page (redirect: true); completion happens on
      // /payment/callback -> POST /payments/user/payment/verify, same as
      // every other course payment.
    } catch (err) {
      console.error("EMI payment error:", err);
      alert(err.response?.data?.message || err.message || 'Payment failed. Please try again.');
      setPaying(false);
      // The EMI may have been paid / changed meanwhile - show the real state
      if (['EMI_ALREADY_PAID', 'NO_PENDING_EMIS', 'NOT_NEXT_EMI'].includes(err.response?.data?.code)) {
        fetchEmiStatus();
      }
    }
  };

  const getStatusInfo = () => {
    if (!emiStatus) return { type: 'active', icon: FaCheckCircle, text: 'Active' };
    
    if (emiStatus.overdueEmis > 0) {
      return { type: 'overdue', icon: FaExclamationTriangle, text: 'Overdue Payments' };
    } else if (emiStatus.gracePeriodEmis > 0) {
      return { type: 'grace', icon: FaClock, text: 'Grace Period' };
    } else {
      return { type: 'active', icon: FaCheckCircle, text: 'Active' };
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <ContentWrapper>
          <LoadingSpinner />
        </ContentWrapper>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <ContentWrapper>
          <BackButton onClick={() => navigate(-1)}>
            <FaArrowLeft /> Go Back
          </BackButton>
          <Card>
            <Title> Error</Title>
            <SubTitle>{error}</SubTitle>
          </Card>
        </ContentWrapper>
      </PageContainer>
    );
  }

  const statusInfo = getStatusInfo();
  const StatusIcon = statusInfo.icon;

  // Pre-payment (nothing overdue): backend tells us the next EMI and whether it can be paid now
  const isOverdueMode = (emiStatus?.totalOverdue || 0) > 0;
  const nextEmi = emiStatus?.nextEmi;
  const nextEmiState = isOverdueMode ? 'overdue' : emiStatus?.nextEmiState;
  const canPayEarly = nextEmiState === 'payable' && !!nextEmi?.emiId;
  const formatLongDate = (d) =>
    new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <PageContainer>
      <ContentWrapper>
        <BackButton onClick={() => navigate(`/course/${courseId}/content`)}>
          <FaArrowLeft /> Back to Course
        </BackButton>

        <Card>
          <Title> EMI Payment</Title>
          <SubTitle>
            {isOverdueMode
              ? 'Clear your pending payments to restore course access'
              : 'Pay your next EMI ahead of its due date. Your course stays unlocked.'}
          </SubTitle>

          <StatusBadge className={statusInfo.type}>
            <StatusIcon />
            {statusInfo.text}
          </StatusBadge>

          {emiStatus && (
            <>
              <DetailsGrid>
                <DetailCard bgColor="#fee2e2" borderColor="#fca5a5">
                  <DetailLabel>Overdue Payments</DetailLabel>
                  <DetailValue color="#991b1b">
                    {emiStatus.overdueEmis || 0}
                  </DetailValue>
                </DetailCard>

                <DetailCard bgColor="#d1fae5" borderColor="#6ee7b7">
                  <DetailLabel>{isOverdueMode ? 'Amount Due' : 'Next EMI'}</DetailLabel>
                  <DetailValue color="#065f46">
                    ₹{(isOverdueMode ? emiStatus.totalOverdue : (nextEmi?.amount || 0)).toLocaleString('en-IN')}
                  </DetailValue>
                </DetailCard>

                <DetailCard bgColor="#dbeafe" borderColor="#93c5fd">
                  <DetailLabel>Paid EMIs</DetailLabel>
                  <DetailValue color="#1e40af">
                    {emiStatus.paidEmis || 0}/{emiStatus.totalEmis || 0}
                  </DetailValue>
                </DetailCard>

                <DetailCard bgColor="#fef3c7" borderColor="#fcd34d">
                  <DetailLabel>Next Due</DetailLabel>
                  <DetailValue color="#92400e" style={{ fontSize: '1rem' }}>
                    {emiStatus.nextDueDate 
                      ? new Date(emiStatus.nextDueDate).toLocaleDateString('en-IN')
                      : 'N/A'}
                  </DetailValue>
                </DetailCard>
              </DetailsGrid>

              {/* Monthly EMI Breakdown */}
              {emiStatus.monthlyAmount > 0 && (() => {
                const firstEmi = emiStatus.emis?.[0];
                const bd = firstEmi?.breakdown;
                const baseValue = bd?.baseValue || Math.round(emiStatus.monthlyAmount / 1.20);
                const cgst = bd?.cgst || Math.round(baseValue * 0.09);
                const sgst = bd?.sgst || Math.round(baseValue * 0.09);
                const gstTotal = bd?.gstTotal || (cgst + sgst);
                const txnFee = bd?.transactionFee || Math.round(baseValue * 0.02);
                return (
                  <div style={{
                    background: '#f0f4ff',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    marginBottom: '2rem',
                    border: '2px solid #c7d2fe',
                  }}>
                    <h3 style={{ fontSize: '1.1rem', marginBottom: '1rem', color: '#4c1d95' }}>
                      Monthly EMI Breakdown
                    </h3>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '0.95rem' }}>
                      <span style={{ color: '#555' }}>Course Value</span>
                      <span>₹{baseValue.toLocaleString('en-IN')}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '0.95rem' }}>
                      <span style={{ color: '#555' }}>CGST (9%)</span>
                      <span>₹{cgst.toLocaleString('en-IN')}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '0.95rem' }}>
                      <span style={{ color: '#555' }}>SGST (9%)</span>
                      <span>₹{sgst.toLocaleString('en-IN')}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '0.95rem', fontWeight: '600', color: '#4f46e5' }}>
                      <span>Total GST (18%)</span>
                      <span>₹{gstTotal.toLocaleString('en-IN')}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '0.95rem' }}>
                      <span style={{ color: '#555' }}>Transaction Fee (2%)</span>
                      <span>₹{txnFee.toLocaleString('en-IN')}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '8px', borderTop: '1px solid #c7d2fe', fontSize: '1rem', fontWeight: '700' }}>
                      <span>Monthly EMI</span>
                      <span>₹{emiStatus.monthlyAmount?.toLocaleString('en-IN')}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '12px', paddingTop: '8px', borderTop: '2px solid #c7d2fe', fontSize: '1.1rem', fontWeight: '800', color: '#1a202c' }}>
                      <span>Total ({emiStatus.tenure || emiStatus.totalEmis} months)</span>
                      <span>₹{(emiStatus.totalAmount || 0).toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                );
              })()}

              {emiStatus.overdueEmis > 0 && (
                <div style={{
                  background: '#fef3c7',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  marginBottom: '2rem',
                  border: '2px solid #fcd34d',
                }}>
                  <p style={{
                    fontSize: '1rem',
                    color: '#92400e',
                    margin: 0,
                    lineHeight: 1.6,
                  }}>
                    <strong> Action Required:</strong> You have {emiStatus.overdueEmis} overdue payment(s). 
                    Please clear ₹{(emiStatus.totalOverdue || 0).toLocaleString('en-IN')} to restore your course access immediately.
                  </p>
                </div>
              )}

              {!isOverdueMode && nextEmiState === 'none_pending' && (
                <div style={{ background: '#d1fae5', borderRadius: '12px', padding: '1.25rem', marginBottom: '1.5rem', border: '2px solid #6ee7b7', color: '#065f46' }}>
                  There are no pending EMIs for this course.
                </div>
              )}

              {!isOverdueMode && nextEmiState === 'too_early' && nextEmi && (
                <div style={{ background: '#dbeafe', borderRadius: '12px', padding: '1.25rem', marginBottom: '1.5rem', border: '2px solid #93c5fd', color: '#1e40af' }}>
                  Your next EMI of ₹{nextEmi.amount.toLocaleString('en-IN')} is due on {formatLongDate(nextEmi.dueDate)}.
                  You can pay it from {formatLongDate(nextEmi.payableFrom)}.
                </div>
              )}

              {!isOverdueMode && canPayEarly && (
                <div style={{ background: '#ecfdf5', borderRadius: '12px', padding: '1.25rem', marginBottom: '1.5rem', border: '2px solid #6ee7b7', color: '#065f46' }}>
                  Paying your next EMI (due {formatLongDate(nextEmi.dueDate)}) now keeps your course unlocked.
                </div>
              )}

              {!isOverdueMode && !canPayEarly && nextEmiState !== 'none_pending' && nextEmiState !== 'too_early' && (
                <div style={{ background: '#fef3c7', borderRadius: '12px', padding: '1.25rem', marginBottom: '1.5rem', border: '2px solid #fcd34d', color: '#92400e' }}>
                  Online payment is not available for this EMI right now. Please contact support if you need help.
                </div>
              )}

              {(isOverdueMode || canPayEarly) && (
                <PayButton
                  onClick={handlePayment}
                  disabled={paying}
                >
                  {paying
                    ? 'Processing...'
                    : isOverdueMode
                      ? ` Pay ₹${(emiStatus.totalOverdue || 0).toLocaleString('en-IN')} Now`
                      : ` Pay ₹${nextEmi.amount.toLocaleString('en-IN')} - Next EMI`}
                </PayButton>
              )}

              <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
                <a
                  href="/user-payment"
                  style={{
                    color: '#667eea',
                    textDecoration: 'none',
                    fontWeight: 600,
                    fontSize: '1rem',
                  }}
                >
                   View Full EMI Dashboard
                </a>
              </div>
            </>
          )}
        </Card>

        {/* Info Card */}
        <Card style={{ background: '#e0e7ff', borderLeft: '4px solid #667eea' }}>
          <h3 style={{ fontSize: '1.3rem', marginBottom: '1rem', color: '#4c1d95' }}> What happens after payment?</h3>
          <ul style={{ fontSize: '1rem', lineHeight: 1.8, paddingLeft: '1.5rem', color: '#1e293b' }}>
            <li>Your payment will be processed immediately</li>
            <li>Course access will be automatically restored</li>
            <li>You'll receive a confirmation email</li>
            <li>You can continue learning without interruption</li>
          </ul>
        </Card>
      </ContentWrapper>
    </PageContainer>
  );
};

export default EMIPaymentPage;
